<template>
  <div class="background-modal">
    <div class="wrapper-modal">
      <div class="wrapper-title">
        <p class="title">Información de campaña</p>
        <CloseButton @click="closeModal" />
      </div>
      <div class="wrapper-form">
        <GeneralInput
          v-model:text="name.val"
          :value="name.val"
          :label="'Nombre'"
          :has-error="name.enabled"
          :error="name.errors.length === 0 ? '' : name.errors[0]"
          class="input-name"
          :is-disabled="isEdit"
        />
        <div class="wrapper-image upload1">
          <div class="info">
            <p>Imagen campaña</p>
            <SecondaryButton
              :buttonLabel="'Subir imagen'"
              @click="$refs.fileIcon.click()"
            />
            <input
              type="file"
              ref="fileIcon"
              hidden
              @change="uploadIcon"
              @click="$event.target.value = ''"
            />
          </div>
        </div>
        <div class="wrapper-campaign-image position1">
          <figure class="grid-image">
            <img
              v-if="showIconPreview"
              :src="previewIcon"
              class="campaign-image"
            />
            <img
              v-else-if="showIcon"
              :src="campaign.icon"
              class="campaign-image"
            />
            <p v-else class="no-image">Sin imagen</p>
          </figure>
          <DeleteButton
            v-if="showIconPreview"
            class="delete"
            @click="removeUploadIcon"
          />
        </div>
        <div class="wrapper-image upload2">
          <div class="info">
            <p>Imagen 1</p>
            <SecondaryButton
              :buttonLabel="'Subir imagen'"
              @click="$refs.fileImage1.click()"
            />
            <input
              type="file"
              ref="fileImage1"
              hidden
              @change="uploadImage1"
              @click="$event.target.value = ''"
            />
          </div>
        </div>

        <div class="wrapper-campaign-image position2">
          <figure class="grid-image">
            <img
              v-if="showImage1Preview"
              :src="previewImage1"
              class="campaign-image"
            />
            <img
              v-else-if="campaign?.additional_image1"
              :src="campaign.additional_image1"
              class="campaign-image"
            />
            <p v-else class="no-image">
              Sin imagen.<br />
              Necesaria para verse en la app móvil.
            </p>
          </figure>
          <DeleteButton
            v-if="showImage1Preview"
            class="delete"
            @click="removeUploadImage1"
          />
          <DeleteButton
            v-if="campaign?.additional_image1"
            class="delete"
            @click="deleteAdditionalImage(1)"
          />
        </div>

        <div class="wrapper-image upload3">
          <div class="info">
            <p>Imagen 2 (opcional)</p>
            <SecondaryButton
              :buttonLabel="'Subir imagen'"
              @click="$refs.fileImage2.click()"
            />
            <input
              type="file"
              ref="fileImage2"
              hidden
              @change="uploadImage2"
              @click="$event.target.value = ''"
            />
          </div>
        </div>

        <div class="wrapper-campaign-image position3">
          <figure class="grid-image">
            <img
              v-if="showImage2Preview"
              :src="previewImage2"
              class="campaign-image"
            />
            <img
              v-else-if="campaign?.additional_image2"
              :src="campaign.additional_image2"
              class="campaign-image"
            />
            <p v-else class="no-image">Sin imagen</p>
          </figure>
          <DeleteButton
            v-if="showImage2Preview"
            class="delete"
            @click="removeUploadImage2"
          />
          <DeleteButton
            v-if="campaign?.additional_image2"
            class="delete"
            @click="deleteAdditionalImage(2)"
          />
        </div>
      </div>
      <StandardButton
        :buttonLabel="'Guardar'"
        class="margin-button"
        :is-disabled="disabledForm"
        @click="createEditCampaign"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, Ref } from "vue";
import GeneralInput from "@/components/commons/controls/GeneralInput.vue";
import { createEditCampaignValidator } from "@/ux/campaigns/validators/createEditCampaignValidator";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import SecondaryButton from "@/components/commons/controls/SecondaryButton.vue";
import CloseButton from "@/components/commons/controls/CloseButton.vue";
import { Campaign } from "@/business/campaign/domain/campaign";
import { convertToBase64 } from "@/business/shared/commons/multimedia/convertToBase64";
import DeleteButton from "@/components/commons/controls/DeleteButton.vue";

export default defineComponent({
  name: "ModalCreateEditCampaign",
  props: {
    campaign: {
      type: Object as () => Campaign | null,
      required: true,
    },
  },
  components: {
    DeleteButton,
    GeneralInput,
    StandardButton,
    SecondaryButton,
    CloseButton,
  },

  setup(props, context) {
    const { name, successForm, reset } = createEditCampaignValidator();

    const icon: Ref<File | null> = ref(null);
    const showIconPreview: Ref<boolean> = ref(false);
    const previewIcon: Ref<string> = ref("");
    const image1: Ref<File | null> = ref(null);
    const showImage1Preview: Ref<boolean> = ref(false);
    const previewImage1: Ref<string> = ref("");
    const image2: Ref<File | null> = ref(null);
    const showImage2Preview: Ref<boolean> = ref(false);
    const previewImage2: Ref<string> = ref("");
    const isEdit: Ref<boolean> = ref(false);

    function closeModal() {
      reset();
      removeFiles();
      context.emit("close");
    }

    async function uploadIcon(event: any) {
      icon.value = event.target.files[0];
      if (icon.value !== null) {
        previewIcon.value = await convertToBase64.execute(icon.value);
        showIconPreview.value = true;
      }
    }

    function removeUploadIcon(): void {
      previewIcon.value = "";
      icon.value = null;
      showIconPreview.value = false;
    }

    async function uploadImage1(event: any) {
      image1.value = event.target.files[0];
      if (image1.value !== null) {
        previewImage1.value = await convertToBase64.execute(image1.value);
        showImage1Preview.value = true;
      }
    }

    function removeUploadImage1(): void {
      previewImage1.value = "";
      image1.value = null;
      showImage1Preview.value = false;
    }

    async function uploadImage2(event: any) {
      image2.value = event.target.files[0];
      if (image2.value !== null) {
        previewImage2.value = await convertToBase64.execute(image2.value);
        showImage2Preview.value = true;
      }
    }

    function removeUploadImage2(): void {
      previewImage2.value = "";
      image2.value = null;
      showImage2Preview.value = false;
    }

    function removeFiles(): void {
      removeUploadIcon();
      removeUploadImage1();
      removeUploadImage2();
    }

    function createEditCampaign(): void {
      if (!isEdit.value) {
        createCampaign();
      } else {
        // reset();
        editCampaign();
      }
    }

    function createCampaign() {
      const newCampaignForm = {
        name: name.value.val,
        icon: icon.value,
        image1: image1.value,
        image2: image2.value,
      };

      context.emit("create", newCampaignForm);
      removeFiles();
    }

    function editCampaign() {
      const editCampaignForm = {
        name: name.value.val,
        icon: icon.value,
        image1: image1.value,
        image2: image2.value,
      };

      context.emit("edit", editCampaignForm);
      removeFiles();
    }

    function deleteAdditionalImage(position: number) {
      context.emit("remove", position);
    }

    const showIcon = computed(() => isEdit.value && !showIconPreview.value);
    const showImage1 = computed(() => isEdit.value && !showImage1Preview.value);
    const showImage2 = computed(() => isEdit.value && !showImage2Preview.value);

    const disabledForm = computed(() => {
      return isEdit.value
        ? icon.value == null && image1.value == null && image2.value == null
        : successForm.value || icon.value == null;
    });

    watch(
      () => props.campaign,
      () => {
        if (props.campaign != null) {
          name.value.val = props.campaign?.campaignName ?? "";
          isEdit.value = true;
        } else {
          reset();
          isEdit.value = false;
        }
      },
      { immediate: true }
    );

    return {
      closeModal,

      icon,
      image1,
      image2,

      name,
      successForm,
      reset,

      showIcon,
      showImage1,
      showImage2,

      disabledForm,
      createEditCampaign,
      isEdit,

      showIconPreview,
      previewIcon,
      showImage1Preview,
      previewImage1,
      showImage2Preview,
      previewImage2,
      uploadIcon,
      removeUploadIcon,
      uploadImage1,
      removeUploadImage1,
      uploadImage2,
      removeUploadImage2,
      deleteAdditionalImage,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/campaigns/styles/modalCreateCampaignStyle";
</style>
